// OGC-478: when a campaign's Meta credential continuity is refused and the
// server queued (or found) a Meta resource refresh, the response carries
// `meta_campaign_credential_continuity_refreshing`. The page re-reads on its
// own a bounded number of times so the controls can come back without anyone
// pressing Refresh, and never polls indefinitely. After the bound the page
// asks the viewer to refresh instead of promising automatic recovery: the
// resync can outlast the re-reads, or end in a refusal that needs a company
// owner or admin.
export const CONTINUITY_REFRESHING_CODE = 'meta_campaign_credential_continuity_refreshing';
export const MAX_CONTINUITY_REFETCHES = 2;
export const CONTINUITY_SUMMARY_REFETCH_DELAY_MS = 30_000;

// The server's suggested wait, clamped to 5..60 s (30 s when absent).
export function continuityRefetchDelayMs(retryAfterSeconds) {
  const seconds = Number.isSafeInteger(retryAfterSeconds) && retryAfterSeconds > 0
    ? retryAfterSeconds
    : 30;
  return Math.min(Math.max(seconds, 5), 60) * 1000;
}

export function summaryBatchIsRefreshing(batch) {
  return Array.isArray(batch?.partialCodes) && batch.partialCodes.includes(CONTINUITY_REFRESHING_CODE);
}

// OGC-488: some rows were published with a Meta login Meta can no longer
// confirm. Unlike refreshing this never clears on its own, so it is never
// re-read automatically.
export const CONTINUITY_ORIGINAL_UNPROVABLE_CODE = 'meta_campaign_credential_continuity_original_unprovable';

export function summaryBatchHasUnprovableOriginal(batch) {
  return Array.isArray(batch?.partialCodes) && batch.partialCodes.includes(CONTINUITY_ORIGINAL_UNPROVABLE_CODE);
}

// The Details read's refreshing failure, or null. Only the provider error
// carries the server's retry time.
export function detailContinuityRefreshing(detail) {
  const error = detail?.provider?.error;
  if (error?.code !== CONTINUITY_REFRESHING_CODE) return null;
  return Object.freeze({
    retryAfterSeconds: Number.isSafeInteger(error.retryAfterSeconds) ? error.retryAfterSeconds : null,
  });
}

// One pending re-read at a time, at most `max` automatic re-reads per key per
// mounted view: the count lives in the scheduler, which each component
// instance creates for itself, so it survives a scope change within that view
// but not a remount. An attempt is counted only when its timer fires, so an
// unrelated re-read that cancels and re-requests the pending timer (a manual
// Refresh, a post-mutation reload) cannot use up the bound. `cancel()` drops
// the pending re-read (scope change or unmount). Once `exhausted(key)` is
// true nothing re-reads automatically for that key, and the page must stop
// promising automatic recovery.
export function createContinuityRefetchScheduler({
  schedule = (task, delay) => setTimeout(task, delay),
  cancel = (timer) => clearTimeout(timer),
  max = MAX_CONTINUITY_REFETCHES,
} = {}) {
  const counts = new Map();
  let pending = null;
  const fired = (key) => counts.get(key) || 0;
  return Object.freeze({
    request(key, delayMs, task) {
      if (!key || typeof task !== 'function') return false;
      if (fired(key) >= max) return false;
      if (pending) {
        if (pending.key === key) return false;
        cancel(pending.timer);
        pending = null;
      }
      const entry = { key, timer: null };
      entry.timer = schedule(() => {
        if (pending === entry) pending = null;
        counts.set(key, fired(key) + 1);
        task();
      }, delayMs);
      pending = entry;
      return true;
    },
    cancel() {
      if (pending) cancel(pending.timer);
      pending = null;
    },
    count(key) {
      return fired(key);
    },
    exhausted(key) {
      return Boolean(key) && fired(key) >= max;
    },
  });
}
